import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  SelectChangeEvent,
  Tabs,
  Tab,
  CircularProgress,
} from "@mui/material";
import { LuxorHeader } from "./components/LuxorHeader";
import { endpointConfigs } from "./luxorMedSettings";
import { SessionHistory, ResponseHistoryItem } from "./types";
import { SessionSelector } from "./components/SessionSelector";
import { TextEditor } from "./components/TextEditor";
import { ResponseRenderer } from "./components/ResponseRenderer";
import { useAuth } from "@/utils/useAuth";
import { useAxios } from "@/utils/useAxios";
import { useMedicalCareContext } from "@/contexts/MedicalCareContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useDiscussionCaseContext } from "@/contexts/DiscussionCaseContext";
import { useMedicalReportsAgentContext } from "@/contexts/MedicalReportsAgentContext";
import { useGuidanceAgentContext } from "@/contexts/GuidanceAgentContext";
import {
  useFilteredConditions,
  useFilteredAllergies,
  useFilteredImmunizations,
  useFilteredSelectedInterventions,
} from "@/pages/medicalcare/components/RegisterArea/useFilteredMedicalData";
import { WarningNoContract } from "./components/WarningNoContract";
import { isTypeAvailable } from "./isTypeAvailable";
import { useLuxorImportData } from "./useLuxorImportData";
import { ReimportConfirmDialog } from "./components/ReimportConfirmDialog";
import { useFhirData } from "@/utils/useFhirData";
import {
  initializeLuxorMedSession,
  LuxorMedResponse
} from "./utils/manageHistoryLuxorMed";
import { buildLuxorPromptMessage } from "./utils/buildLuxorPromptMessage";
import { migrateLuxorSessionHistory } from "./utils/migrateLuxorSessionHistory";

const createDefaultSession = (appointmentId?: string): SessionHistory => ({
  importedContent: "",
  freeText: "",
  importedSnapshot: "",
  response: "",
  showEditor: true,
  appointmentId,
  timestamp: Date.now(),
});

const getResponseTypeFromSelectedType = (
  selectedType: string
): 'luxor-medical-report' | 'luxor-patient-case-analysis' | 'luxor-patient-guidance' => {
  switch (selectedType) {
    case 'luxor-medical-report':
      return 'luxor-medical-report';
    case 'luxor-patient-case-analysis':
      return 'luxor-patient-case-analysis';
    case 'luxor-patient-guidance':
      return 'luxor-patient-guidance';
    default:
      return 'luxor-medical-report';
  }
};

export default function LuxorMed() {
  const {
    luxorCurrentSession: selectedType,
    setLuxorCurrentSession: setSelectedType,
    luxorSessionsHistory: sessionsHistory,
    setLuxorSessionsHistory: setSessionsHistory,
    luxorResponseHistory: responseHistory,
    setLuxorResponseHistory: setResponseHistory,
    luxorShowEditor: showEditor,
    setLuxorShowEditor: setShowEditor,
    luxorAppointmentHistory,
    addLuxorResponse,
  } = useMedicalCareContext();
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showCurrentSessionHistory, setShowCurrentSessionHistory] = useState(false);
  const [luxorSessionId, setLuxorSessionId] = useState<string | null>(null);

  const { copyMedicalData, appointment, patient, condutas, problems } =
    useMedicalCareContext();
  const { contractDiscussionCase } = useDiscussionCaseContext();
  const { contractMedicalReports } = useMedicalReportsAgentContext();
  const { contractGuidance } = useGuidanceAgentContext();
  const { organizationId } = useOrganizationContext();

  const hasAnyAIContract = useMemo(() => {
    return !!(
      contractDiscussionCase ||
      contractMedicalReports ||
      contractGuidance
    );
  }, [contractDiscussionCase, contractMedicalReports, contractGuidance]);

  const selectedTypeHasContract = isTypeAvailable(
    selectedType,
    contractDiscussionCase,
    contractMedicalReports,
    contractGuidance
  );

  const currentSession =
    sessionsHistory[selectedType] || createDefaultSession(appointment?.id);

  const { importedContent, freeText, importedSnapshot } = currentSession;
  const response = currentSession.response;

  const isImportedDirty =
    importedSnapshot !== "" &&
    importedContent.trim() !== "" &&
    importedContent !== importedSnapshot;

  const promptMessage = buildLuxorPromptMessage(importedContent, freeText);

  const activeSessionTypes = Object.keys(sessionsHistory);

  const currentAppointmentResponses = useMemo(() => {
    return luxorAppointmentHistory.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  }, [luxorAppointmentHistory]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (copied) {
      timer = setTimeout(() => setCopied(false), 3000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [copied]);

  useEffect(() => {
    if (copyMedicalData && copyMedicalData.length > 0) {
      setSessionsHistory((prev) => {
        const latestSession =
          prev[selectedType] || createDefaultSession(appointment?.id);

        return {
          ...prev,
          [selectedType]: {
            ...latestSession,
            importedContent: copyMedicalData,
            importedSnapshot: copyMedicalData,
            showEditor: true,
            appointmentId: appointment?.id,
            timestamp: Date.now(),
          },
        };
      });

      setShowEditor(true);
    }
  }, [copyMedicalData, appointment?.id, selectedType]);

  const handleTypeChange = (event: SelectChangeEvent) => {
    setSelectedType(event.target.value);
  };



  const setImportedContent = (value: string) => {
    setSessionsHistory((prev) => ({
      ...prev,
      [selectedType]: {
        ...currentSession,
        importedContent: value,
      },
    }));
  };

  const setFreeText = (value: string) => {
    setSessionsHistory((prev) => ({
      ...prev,
      [selectedType]: {
        ...currentSession,
        freeText: value,
      },
    }));
  };

  const handleImportSuccess = useCallback(
    (textoImportado: string) => {
      setSessionsHistory((prev) => {
        const latestSession =
          prev[selectedType] || createDefaultSession(appointment?.id);
        return {
          ...prev,
          [selectedType]: {
            ...latestSession,
            importedContent: textoImportado,
            importedSnapshot: textoImportado,
            showEditor: true,
            appointmentId: appointment?.id,
            timestamp: Date.now(),
          },
        };
      });
    },
    [selectedType, appointment?.id, setSessionsHistory]
  );

  const [reimportDialogOpen, setReimportDialogOpen] = useState(false);
  const confirmResolveRef = useRef<((value: "keep" | "replace") => void) | null>(
    null
  );

  const confirmReimport = useCallback(() => {
    return new Promise<"keep" | "replace">((resolve) => {
      confirmResolveRef.current = resolve;
      setReimportDialogOpen(true);
    });
  }, []);

  const handleKeepChanges = () => {
    setReimportDialogOpen(false);
    confirmResolveRef.current?.("keep");
    confirmResolveRef.current = null;
  };

  const handleReplaceData = () => {
    setReimportDialogOpen(false);
    confirmResolveRef.current?.("replace");
    confirmResolveRef.current = null;
  };

  const handleCopyResponse = () => {
    if (response) {
      navigator.clipboard.writeText(response);
      setCopied(true);
    }
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const showPrompt = newValue === 0;
    setShowEditor(showPrompt);
  };

  const activeTab = showEditor ? 0 : 1;

  const { handleImport, imported } = useLuxorImportData({
    onImportSuccess: handleImportSuccess,
    onCopyToEditor: () => setShowEditor(true),
    isImportedDirty,
    confirmReimport,
  });

  useEffect(() => {
    if (appointment?.id && patient?.id) {
      const practitionerParticipant = useFhirData.findParticipant(
        appointment?.participant,
        "Practitioner"
      );
      const practitionerId = practitionerParticipant?.reference?.split("/")?.[1];

      const sessionId = initializeLuxorMedSession(patient.id, practitionerId);
      setLuxorSessionId(sessionId);
    }
  }, [appointment?.id, patient?.id]);

  const handleSubmit = async () => {
    if (!promptMessage.trim()) return;
    try {
      setIsLoading(true);
      setShowEditor(false);
      setSessionsHistory((prev) => ({
        ...prev,
        [selectedType]: {
          ...currentSession,
          response: "",
        },
      }));
      const config = endpointConfigs[selectedType];
      let endpoint = `${import.meta.env.VITE_ENDPOINT_N8N}${config.endpoint}`;
      const requestBody = await config.bodyBuilder(promptMessage, organizationId);

      const response = await useAxios.post(endpoint, requestBody, {
        headers: {
          Authorization: await useAuth.getUserToken(),
        },
        timeout: 120000,
      });

      let responseText = "";
      if (response && response.data) {
        console.log("Processing response data:", response.data);
        if (response.data.output) {
          responseText = response.data.output;
        } else if (response.data.response) {
          responseText = response.data.response;
        } else if (response.data.text) {
          responseText = response.data.text;
        } else if (typeof response.data === "string") {
          responseText = response.data;
        } else {
          responseText = JSON.stringify(response.data);
        }
        console.log("Final response text:", responseText);
      } else {
        console.warn("No response data received");
      }

      if (!responseText) {
        throw new Error("No response text generated from API response");
      }

      const newResponseItem: ResponseHistoryItem = {
        appointmentId: appointment?.id || "desconhecido",
        type: selectedType,
        response: responseText,
        timestamp: Date.now(),
        title: endpointConfigs[selectedType].name,
      };

      setResponseHistory((prev) => [...prev, newResponseItem]);

      const luxorResponse: LuxorMedResponse = {
        responseType: getResponseTypeFromSelectedType(selectedType),
        responseContent: responseText,
        promptContent: promptMessage,
        timestamp: new Date().toISOString(),
        appointmentId: appointment?.id,
        promptVersion: "1.0.0",
        agentProvider: "LuxorMed"
      };

      addLuxorResponse(luxorResponse);

      setSessionsHistory((prev) => {
        const newSessionsHistory = {
          ...prev,
          [selectedType]: {
            ...currentSession,
            response: responseText,
            appointmentId: appointment?.id,
            timestamp: Date.now(),
          },
        };
        return newSessionsHistory;
      });
    } catch (error: any) {
      setShowEditor(false);
      setSessionsHistory((prev) => ({
        ...prev,
        [selectedType]: {
          ...currentSession,
          response:
            "Desculpe, tive um problema ao processar sua mensagem. Por favor, tente novamente.",
        },
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const toggleCurrentSessionHistory = () => {
    setShowCurrentSessionHistory(!showCurrentSessionHistory);
  };

  const handleCopyCurrentResponse = (responseContent: string) => {
    navigator.clipboard.writeText(responseContent);
    setCopied(true);
  };

  const handleLoadResponse = (response: LuxorMedResponse) => {
    setSessionsHistory((prev) => ({
      ...prev,
      [selectedType]: {
        ...currentSession,
        importedContent: response.promptContent,
        freeText: "",
        importedSnapshot: "",
        response: response.responseContent,
        showEditor: true,
        appointmentId: appointment?.id,
        timestamp: Date.now(),
      },
    }));

    setShowEditor(true);
  };

  return (
    <Box>
      <LuxorHeader
        selectedType={selectedType}
        endpointConfigs={endpointConfigs}
        onTypeChange={handleTypeChange}
        showImportButton={false}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          width: "100%",
        }}
      >
        {/* {currentAppointmentResponses.length > 0 && (
          <Actions
            showCurrentSessionHistory={showCurrentSessionHistory}
            toggleCurrentSessionHistory={toggleCurrentSessionHistory}
            hasCurrentSessionHistory={currentAppointmentResponses.length > 0}
          />
        )} */}
      </Box>

      {/* Histórico do atendimento atual */}
      {/* {showCurrentSessionHistory && currentAppointmentResponses.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <CurrentSessionHistory
            responses={currentAppointmentResponses}
            onCopyResponse={handleCopyCurrentResponse}
            onLoadResponse={handleLoadResponse}
          />
        </Box>
      )} */}

      {/* {showResponseHistory && responseHistory.length > 0 && (
        <ResponseHistory
          responseHistory={responseHistory}
          deleteHistoryItem={deleteHistoryItem}
        />
      )} */}

      {hasAnyAIContract && selectedTypeHasContract ? (
        <>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              mb: 2,
              minHeight: 36,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                minHeight: 36,
              },
            }}
          >
            <Tab label="Prompt" />
            <Tab
              label="Resposta da IA"
              disabled={!response && !isLoading}
              icon={
                isLoading ? (
                  <CircularProgress size={14} sx={{ color: 'primary.main' }} />
                ) : undefined
              }
              iconPosition="end"
            />
          </Tabs>

          {activeTab === 0 && (
            <TextEditor
              importedContent={importedContent}
              freeText={freeText}
              onImportedChange={setImportedContent}
              onFreeTextChange={setFreeText}
              onSubmit={handleSubmit}
              isLoading={isLoading}
              onImport={handleImport}
              imported={imported}
              isImportedDirty={isImportedDirty}
            />
          )}

          {activeTab === 1 && (
            <ResponseRenderer
              response={response}
              copied={copied}
              onCopy={handleCopyResponse}
              isLoading={isLoading}
              appointmentId={currentSession.appointmentId}
            />
          )}
        </>
      ) : !selectedTypeHasContract ? (
        <WarningNoContract />
      ) : (
        <WarningNoContract />
      )}

      <ReimportConfirmDialog
        open={reimportDialogOpen}
        onKeep={handleKeepChanges}
        onReplace={handleReplaceData}
      />
    </Box>
  );
}
